import { config } from '@/configs'

import * as Sentry from '@sentry/react'

// Temporary instrumentation: to remove once the migration to `useAuthorizedFileUrl` /
// `isFileUrlProxied` (see @/shared/helpers/protectedFile) is confirmed complete for every file
// call site and the monitoring period has elapsed (BO-05 of the secure file proxy plan).

// Category A: requests issued through the DOM/fetch/XHR (img, iframe, link, fetch, xhr, ...)
const setupResourceObserver = () => {
  if (typeof PerformanceObserver === 'undefined') {
    return
  }

  const observer = new PerformanceObserver(list => {
    list.getEntries().forEach(entry => {
      if (_isBlobStorageUrl(entry.name)) {
        _logDirectBlobStorageCall('resource request', entry.name, {
          initiatorType: (entry as PerformanceResourceTiming).initiatorType,
        })
      }
    })
  })

  observer.observe({
    type: 'resource', // all PerformanceResourceTiming entries (img, css, script, font, fetch, xhr, iframe, ...);
    // there's no initiatorType filter here, matching is done purely by URL host in _isBlobStorageUrl below
    buffered: true, // also replay entries recorded before this observer was created
  })
}

// Category B: navigations to a new browsing context via window.open(...), from any call site.
// Patched once globally so no individual component needs its own logging code.
const setupWindowOpenLogging = () => {
  if (typeof window === 'undefined') {
    return
  }

  const nativeOpen = window.open.bind(window)

  window.open = (url?: string | URL, target?: string, features?: string) => {
    const urlString = url?.toString()
    if (urlString !== undefined && _isBlobStorageUrl(urlString)) {
      _logDirectBlobStorageCall('window.open', urlString)
    }

    return nativeOpen(url, target, features)
  }
}

// Category B: navigations triggered by clicking a native <a href="..."> link, from anywhere in
// the document. A single capturing listener catches every anchor, current and future.
const setupAnchorClickLogging = () => {
  if (typeof document === 'undefined') {
    return
  }

  document.addEventListener(
    'click',
    event => {
      const eventTarget = event.target as HTMLElement | null
      const anchor = eventTarget?.closest('a[href]')
      const href = anchor?.getAttribute('href')

      if (href !== null && href !== undefined && _isBlobStorageUrl(href)) {
        _logDirectBlobStorageCall('anchor navigation', href)
      }
    },
    { capture: true }, // capture phase: runs before any component's own click handler can stopPropagation()
  )
}

// Note: direct assignment to window.location (`window.location.href = url`, `location.assign()`,
// `location.replace()`) cannot be reliably intercepted in browsers and isn't covered here.
// Audit manually for this pattern if it's used in the codebase.
export const setupBlobStorageCallLogging = () => {
  setupResourceObserver()
  setupWindowOpenLogging()
  setupAnchorClickLogging()
}

// Suffix of Azure Blob Storage hosts, e.g. apeazstaarc02d.blob.core.windows.net
const BLOB_STORAGE_HOST_SUFFIX = '.blob.core.windows.net'

const _isBlobStorageUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.origin).hostname.endsWith(BLOB_STORAGE_HOST_SUFFIX)
  } catch {
    return false
  }
}

// Azure Blob Storage URLs carry a SAS token in the query string — never log it verbatim, it's a
// live, time-limited credential. Keep only origin + pathname.
const _sanitizeBlobUrlForLogging = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return '[invalid-url]'
  }
}

const _getCurrentPage = (): string =>
  typeof window === 'undefined' ? '[unknown-page]' : window.location.pathname

const _logDirectBlobStorageCall = (
  reason: string,
  url: string,
  extra: Record<string, unknown> = {},
) => {
  const payload = {
    url: _sanitizeBlobUrlForLogging(url),
    page: _getCurrentPage(),
    ...extra,
  }

  if (['L', 'D', 'U'].includes(config.env)) {
    // eslint-disable-next-line no-console
    console.info(`Direct call to blob storage URL (${reason})`, payload)
  }

  Sentry.captureMessage(`Direct call to blob storage URL (${reason})`, {
    level: 'info',
    tags: { feature: 'blobStorageCallLogging' },
    extra: payload,
  })
}
